import { Popover } from '@material-ui/core';
import moment from 'moment';
import React, { useState } from 'react';
import { SingleDatePicker } from 'react-dates';

import {
  determinePillBGColor,
  determinePillTextColor,
} from '~/src/dash/components/dropdowns/common/style';
import {
  borderRadius,
  Col,
  Cols,
  ITProps,
  Row,
  Rows,
  spacing,
  Text,
  TextButton,
  TKey,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { isLanguageOversized } from '~/src/locales/common';
import { IStartToEndDateRanges } from '~/src/dash/components/calendar/common';
import { unstable_batchedUpdates } from 'react-dom';

const LABEL_WIDTH = '120px';

type TFocusedInput = 'start-date-from' | 'start-date-to' | 'end-date-from' | 'end-date-to' | null;

const generateDateRangeLabel = (
  t: (key: TKey, props?: ITProps) => string,
  startDateFrom: moment.Moment | null,
  startDateTo: moment.Moment | null,
  endDateFrom: moment.Moment | null,
  endDateTo: moment.Moment | null,
): string => {
  if (!startDateFrom && !startDateTo && !endDateFrom && !endDateTo) {
    return `${t('StartDate')} - ${t('EndDate')}`;
  }

  const startFromLabel = startDateFrom ? moment(startDateFrom).format('MM/DD/YYYY') : '';
  const startToLabel = startDateTo ? moment(startDateTo).format('MM/DD/YYYY') : '';
  const endFromLabel = endDateFrom ? moment(endDateFrom).format('MM/DD/YYYY') : '';
  const endToLabel = endDateTo ? moment(endDateTo).format('MM/DD/YYYY') : '';

  let startLabel = t('Start');
  let endLabel = '';

  if (startFromLabel || startToLabel) {
    startLabel += ` ${startFromLabel}${startFromLabel && startToLabel ? ' - ' : ''}${startToLabel}`;
  }

  if (endFromLabel || endToLabel) {
    endLabel = ` - ${t('End')} ${endFromLabel}${
      endFromLabel && endToLabel ? ' - ' : ''
    }${endToLabel}`;
  }

  return `${startLabel}${endLabel}`;
};

interface IProps {
  defaultBGColor?: string;
  defaultTextColor?: string;
  hoverBGColor?: string;
  hoverTextColor?: string;
  isDisabled?: boolean;
  key?: string | number; // used for react purposes
  handleApply: (ranges: IStartToEndDateRanges) => void;
  hasError?: boolean;
  selectedRanges: IStartToEndDateRanges;
}

export const StartToEndDateRangePicker = ({
  defaultBGColor = publicaColors.veryLightGray,
  defaultTextColor = publicaColors.black,
  hoverBGColor = publicaColors.lightGray,
  hoverTextColor = publicaColors.black,
  isDisabled = false,
  hasError = false,
  handleApply,
  selectedRanges,
}: IProps): React.ReactNode => {
  const { t, i18n } = usePublicaTranslation();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [focusedInput, setFocusedInput] = useState<TFocusedInput>(null);

  const [startDateFrom, setStartDateFrom] = useState<moment.Moment | null>(
    selectedRanges.startDateFrom,
  );
  const [startDateTo, setStartDateTo] = useState<moment.Moment | null>(selectedRanges.startDateTo);
  const [endDateFrom, setEndDateFrom] = useState<moment.Moment | null>(selectedRanges.endDateFrom);
  const [endDateTo, setEndDateTo] = useState<moment.Moment | null>(selectedRanges.endDateTo);

  const isActive = Boolean(
    selectedRanges.startDateFrom ||
      selectedRanges.startDateTo ||
      selectedRanges.endDateFrom ||
      selectedRanges.endDateTo,
  );

  const color = hasError
    ? publicaColors.red
    : determinePillTextColor(
        isDisabled,
        isActive,
        Boolean(anchorEl),
        isHovered,
        defaultTextColor,
        hoverTextColor,
      );

  const handleClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const onFocusChange = ({
    focused,
    focusedInput,
  }: {
    focused: boolean;
    focusedInput: TFocusedInput;
  }): void => {
    if (focused) {
      setFocusedInput(focusedInput);
    } else {
      setFocusedInput(null);
    }
  };

  const handleClear = (): void => {
    unstable_batchedUpdates(() => {
      setStartDateFrom(null);
      setStartDateTo(null);
      setEndDateFrom(null);
      setEndDateTo(null);
    });
  };

  const handleClose = (): void => {
    handleApply({
      startDateFrom,
      startDateTo,
      endDateFrom,
      endDateTo,
    });
    setAnchorEl(null);
  };

  return (
    <div>
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleClick}
        style={{
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          borderRadius: borderRadius.extraLarge,
          backgroundColor: hasError
            ? publicaColors.veryLightRed
            : determinePillBGColor(
                isDisabled,
                isActive,
                Boolean(anchorEl),
                isHovered,
                defaultBGColor,
                hoverBGColor,
              ),
        }}
      >
        <Cols
          gap={spacing.small}
          style={{
            alignItems: 'center',
            height: '32px',
            padding: `${spacing.extraSmall} ${spacing.base}`,
            margin: '2px 0px 2px 0px',
          }}
        >
          <Col>
            <Text color={color} bold extraSmall style={{ lineHeight: 1 }}>
              {generateDateRangeLabel(t, startDateFrom, startDateTo, endDateFrom, endDateTo)}
            </Text>
          </Col>
        </Cols>
      </div>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        style={{ marginTop: '4px' }}
        disablePortal={false}
        PaperProps={{
          style: {
            overflow: 'visible',
            boxShadow: '0px 3px 6px rgba(0,0,0,0.16)',
          },
        }}
        BackdropProps={{
          style: {
            backgroundColor: 'transparent',
          },
        }}
      >
        <Rows horizontalPadding smallVerticalPadding>
          <Row noPadding>
            <Cols
              wrap
              style={{
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Col width={LABEL_WIDTH}>
                <Text small tKey='StartDateRange' />
              </Col>
              <Col>
                <div
                  style={{
                    transform: 'scale(0.90)',
                    transformOrigin: 'right center',
                    zIndex: 999,
                    position: 'relative',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <SingleDatePicker
                    id='start-date-from'
                    placeholder={t('From')}
                    onDateChange={(date) => {
                      setStartDateFrom(date);
                    }}
                    onFocusChange={({ focused }) =>
                      onFocusChange({ focused, focusedInput: 'start-date-from' })
                    }
                    date={startDateFrom}
                    small
                    calendarInfoPosition='bottom'
                    monthFormat={t('FormatMonthNameYear')}
                    focused={focusedInput === 'start-date-from'}
                    isOutsideRange={() => false}
                  />
                </div>
              </Col>
              <Col smallHorizontalPadding>
                <Text small>-</Text>
              </Col>
              <Col>
                <div
                  style={{
                    transform: 'scale(0.90)',
                    transformOrigin: 'left center',
                    zIndex: 999,
                    position: 'relative',
                  }}
                >
                  <SingleDatePicker
                    id='start-date-to'
                    placeholder={t('To')}
                    onDateChange={(date) => {
                      setStartDateTo(date);
                    }}
                    onFocusChange={({ focused }) =>
                      onFocusChange({ focused, focusedInput: 'start-date-to' })
                    }
                    date={startDateTo}
                    small
                    calendarInfoPosition='bottom'
                    monthFormat={t('FormatMonthNameYear')}
                    focused={focusedInput === 'start-date-to'}
                    isOutsideRange={(date) => {
                      if (startDateFrom && date < startDateFrom) return true;
                      if (endDateFrom && date > endDateFrom) return true;
                      return false;
                    }}
                  />
                </div>
              </Col>
            </Cols>
          </Row>
          <Row noPadding>
            <Cols
              wrap
              style={{
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Col width={LABEL_WIDTH}>
                <Text small tKey='EndDateRange' />
              </Col>
              <Col>
                <div
                  style={{
                    transform: 'scale(0.90)',
                    transformOrigin: 'right center',
                    zIndex: 888,
                    position: 'relative',
                  }}
                >
                  <SingleDatePicker
                    id='end-date-from'
                    placeholder={t('From')}
                    onDateChange={(date) => {
                      setEndDateFrom(date);
                    }}
                    onFocusChange={({ focused }) =>
                      onFocusChange({ focused, focusedInput: 'end-date-from' })
                    }
                    date={endDateFrom}
                    small
                    calendarInfoPosition='bottom'
                    monthFormat={t('FormatMonthNameYear')}
                    focused={focusedInput === 'end-date-from'}
                    isOutsideRange={(date) => {
                      if (startDateFrom && date < startDateFrom) return true;
                      if (startDateTo && date < startDateTo) return true;
                      return false;
                    }}
                  />
                </div>
              </Col>
              <Col smallHorizontalPadding>
                <Text small>-</Text>
              </Col>
              <Col>
                <div
                  style={{
                    transform: 'scale(0.90)',
                    transformOrigin: 'left center',
                    zIndex: 888,
                    position: 'relative',
                  }}
                >
                  <SingleDatePicker
                    id='end-date-to'
                    placeholder={t('To')}
                    onDateChange={(date) => {
                      setEndDateTo(date);
                    }}
                    onFocusChange={({ focused }) =>
                      onFocusChange({ focused, focusedInput: 'end-date-to' })
                    }
                    date={endDateTo}
                    small
                    calendarInfoPosition='bottom'
                    monthFormat={t('FormatMonthNameYear')}
                    focused={focusedInput === 'end-date-to'}
                    isOutsideRange={(date) => {
                      if (endDateFrom && date < endDateFrom) return true;
                      if (startDateFrom && date < startDateFrom) return true;
                      if (startDateTo && date < startDateTo) return true;
                      return false;
                    }}
                  />
                </div>
              </Col>
            </Cols>
          </Row>
          <Row
            noVerticalPadding
            smallHorizontalPadding
            style={{
              minHeight: '32px',
              width: '100%',
              justifyContent: 'flex-end',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <TextButton
              size={isLanguageOversized(i18n.language) ? 'extraSmall' : 'small'}
              onClick={handleClear}
            >
              {t('Clear')}
            </TextButton>
          </Row>
        </Rows>
      </Popover>
    </div>
  );
};
